import React, { useEffect } from "react";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Login from "./components/auth/Login";
import { ProtectedRoute } from "./components/auth/protectedRoute";
import { Dashboard } from "./components/dashboard/Dashboard";
import { EntityTable } from "./components/entities/EntityTable";
import { OrderTable } from "./components/orders/OrderTable";
import { RejectedOrders } from "./components/orders/RejectedOrders";
import MissingAttachmentsTable from "./components/orders/MissingAttachmentsTable";
import { OrderStatusLookup } from "./components/orders/OrderStatusLookup";
import { PharmacyStats } from "./components/pharmacies/PharmacyStats";
import { UsersTable } from "./components/users/UsersTable";
import PageLayouts from "./components/virtual_pharmacy/PageLayouts";
import { AuthProvider } from "./lib/authContext";
import { ThemeProvider } from "./lib/themeContext";
import { UnAuthorized } from "./components/auth/UnAuthorized";
import { tracker } from "./lib/tracker";
import * as Sentry from "@sentry/react";
import Changelogs from "./components/changelogs/ChangeLogsList";
import ChangeUpdates from "./components/changelogs/ChangeUpdates";
import { CouponsList } from "./components/virtual_pharmacy/CouponsList";
import ShipmentTracker from "./components/orders/ShipmentTracker";
import Queues from "./components/queues";
import MedsMappings from "./components/medsCategories/medsMappings";
import MedsCategoryList from "./components/medsCategories/medsCategoryList";
import MasterMeds from "./components/medsCategories/masterMeds";
import ProductsStock from "./components/medsCategories/productsStock";
import B2CProductsMaster from "./components/medsCategories/b2cProductsMaster";
import B2CProductsMapping from "./components/b2cProducts/b2cProductsMapping";
import B2CProductsManagement from "./components/b2cProductManagement";
import PendingOrders from "./components/b2c/PendingOrders";
import RefundProcessing from "./components/b2c/RefundProcessing";
import CartCharges from "./pages/CartCharges";
import SupportCases from "./components/support/SupportCases";
import NonOperationalDays from "./pages/NonOperationalDays";
import Notifications from "./pages/Notifications";
import { ProviderTable } from "./components/providers/ProviderTable";
import EntityRules from "./pages/EntityRules";
import RolesAndPermissions from "./components/roles/RolesAndPermissions";
import TeamPage from "./components/team/TeamPage";
import AuditLogs from "./pages/AuditLogs";
import BulkActions from "./pages/BulkActions";
import LogisticsPartners from "./pages/LogisticsPartners";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  useEffect(() => {
    tracker.start();
    return () => tracker.stop();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <div className="min-h-screen bg-white dark:bg-gray-900">
          <Router>
            <AuthProvider>
            <Routes>
              <Route
                path="/login"
                element={
                  <Sentry.ErrorBoundary
                    fallback={<div>Something went wrong...</div>}
                  >
                    <Login />
                  </Sentry.ErrorBoundary>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <Dashboard />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <OrderTable />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/rejected_orders"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <RejectedOrders />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/order_status_lookup"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <OrderStatusLookup />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pharmacies"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <PharmacyStats />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entities"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <EntityTable />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/providers"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <ProviderTable />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <UsersTable />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/page_layouts"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <PageLayouts />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/changelogs"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <Changelogs />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/updates"
                element={
                  <Sentry.ErrorBoundary
                    fallback={<div>Something went wrong...</div>}
                  >
                    <ChangeUpdates />
                  </Sentry.ErrorBoundary>
                }
              />
              <Route
                path="/coupons"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <CouponsList />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/shipment_tracker"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <ShipmentTracker />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/missing_attachments"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <MissingAttachmentsTable />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/queues"
                element={
                  <ProtectedRoute>
                    <Queues />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meds_category"
                element={
                  <ProtectedRoute>
                    <MedsCategoryList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/meds_mappings"
                element={
                  <ProtectedRoute>
                    <MedsMappings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products_master"
                element={
                  <ProtectedRoute>
                    <MasterMeds />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/products_stock"
                element={
                  <ProtectedRoute>
                    <ProductsStock />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2c_products_mapping"
                element={
                  <ProtectedRoute>
                    <B2CProductsMapping />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2c_products_master"
                element={
                  <ProtectedRoute>
                    <B2CProductsMaster />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2c_products_management"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <B2CProductsManagement />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2c_pending_orders"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <PendingOrders />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/b2c_refunds"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <RefundProcessing />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cart_charges"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <CartCharges />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support_cases"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <SupportCases />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/non_operational_days"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <NonOperationalDays />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <Notifications />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/entity_rules"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <EntityRules />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/roles_permissions"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <RolesAndPermissions />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <TeamPage />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/audit_logs"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <AuditLogs />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk_actions"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <BulkActions />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/logistics_partners"
                element={
                  <ProtectedRoute>
                    <Sentry.ErrorBoundary
                      fallback={<div>Something went wrong...</div>}
                    >
                      <LogisticsPartners />
                    </Sentry.ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/unauthorized"
                element={
                  <Sentry.ErrorBoundary
                    fallback={<div>Something went wrong...</div>}
                  >
                    <UnAuthorized />
                  </Sentry.ErrorBoundary>
                }
              />
            </Routes>
          </AuthProvider>
        </Router>
      </div>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
